import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarClock, Loader2, MessageSquareText, MoreHorizontal, Pencil, Play, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { PageContainer } from '../../../common/components/PageContainer';
import { SidePanel } from '../../../components/design-system/common/SidePanel';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { ConfirmActionDialog } from '../../../components/design-system/admin/ConfirmActionDialog';
import { ArdiChannel, ArdiRoutine, RoutineInput, RoutineTemplate, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { RoutineDraft, RoutineEditor, emptyDraft } from '../../../modules/vendor/routines/RoutineEditor';
import { browserTimezone, scheduleSentence } from '../../../modules/vendor/routines/routine-format';
import { CHANNEL_ICONS } from '../../../modules/vendor/channels/channel-presentation';

/**
 * Standing instructions for Ardi.
 *
 * "Every weekday at 8, give me my brief" — Ardi runs it on time, leaves the
 * answer in its own conversation, tells the bell, and posts it to the chat
 * apps chosen. Each run is one Ardi question and costs what one costs; the
 * page says so where a routine is switched on.
 */
const RoutinesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const api = useArdiApi();
  const lang = (i18n.language || 'en').slice(0, 2);

  const [routines, setRoutines] = useState<ArdiRoutine[] | null>(null);
  const [templates, setTemplates] = useState<RoutineTemplate[]>([]);
  const [limit, setLimit] = useState(10);
  const [channels, setChannels] = useState<ArdiChannel[]>([]);
  const [editing, setEditing] = useState<{ uuid: string | null; draft: RoutineDraft } | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState<string | null>(null);
  const [removing, setRemoving] = useState<ArdiRoutine | null>(null);

  useEffect(() => {
    api.listRoutines()
      .then((r) => {
        setRoutines(r.routines);
        setTemplates(r.templates);
        setLimit(r.limit);
      })
      .catch(() => setRoutines([]));
    api.listChannels().then((r) => setChannels(r.channels)).catch(() => setChannels([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tpl = (id: string, field: 'TITLE' | 'DESC' | 'PROMPT', fallback: string) => t(`ARDI.ROUTINES.TEMPLATE.${id.toUpperCase()}.${field}`, fallback) as string;

  const fromTemplate = (template: RoutineTemplate) => {
    setEditing({
      uuid: null,
      draft: {
        title: tpl(template.id, 'TITLE', template.title),
        prompt: tpl(template.id, 'PROMPT', template.prompt),
        template: template.id,
        schedule: { ...template.schedule, timezone: browserTimezone() },
        deliverTo: [],
        enabled: true,
      },
    });
  };

  const edit = (r: ArdiRoutine) =>
    setEditing({ uuid: r.uuid, draft: { title: r.title, prompt: r.prompt, template: r.template, schedule: r.schedule, deliverTo: r.deliverTo, enabled: r.enabled } });

  const replace = (r: ArdiRoutine) => setRoutines((list) => (list?.some((x) => x.uuid === r.uuid) ? list.map((x) => (x.uuid === r.uuid ? r : x)) : [...(list ?? []), r]));

  const save = async (input: RoutineInput) => {
    if (!editing) return;
    setSaving(true);
    try {
      const saved = editing.uuid ? await api.updateRoutine(editing.uuid, input) : await api.createRoutine(input);
      replace(saved);
      setEditing(null);
      toast.success(t(editing.uuid ? 'ARDI.ROUTINES.SAVED' : 'ARDI.ROUTINES.CREATED', { title: saved.title }));
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r: ArdiRoutine, enabled: boolean) => {
    replace({ ...r, enabled });
    try {
      replace(await api.updateRoutine(r.uuid, { enabled }));
    } catch {
      replace(r);
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const runNow = async (r: ArdiRoutine) => {
    setRunning(r.uuid);
    try {
      const done = await api.runRoutine(r.uuid);
      replace(done);
      if (done.lastStatus === 'ok') toast.success(t('ARDI.ROUTINES.RAN', { title: r.title }));
      else toast.error(t('ARDI.ROUTINES.RUN_FAILED'));
    } catch {
      toast.error(t('ARDI.ROUTINES.RUN_FAILED'));
    } finally {
      setRunning(null);
    }
  };

  const remove = async () => {
    if (!removing) return;
    await api.removeRoutine(removing.uuid);
    setRoutines((list) => list?.filter((x) => x.uuid !== removing.uuid) ?? list);
    setRemoving(null);
  };

  const used = new Set((routines ?? []).map((r) => r.template).filter(Boolean));
  const full = (routines?.length ?? 0) >= limit;
  const channelByUuid = useMemo(() => new Map(channels.map((c) => [c.uuid, c])), [channels]);

  return (
    <PageContainer breadcrumbs={[{ label: t('AGENTS.NAV.HOME', 'Home'), href: '/home/dashboard' }, { label: t('ARDI.ROUTINES.TITLE') }]}>
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">{t('ARDI.ROUTINES.TITLE')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('ARDI.ROUTINES.SUBTITLE')}</p>
          </div>
          <Button variant="outline" onClick={() => setEditing({ uuid: null, draft: emptyDraft() })} disabled={full}>
            <Plus />
            {t('ARDI.ROUTINES.NEW')}
          </Button>
        </div>

        {!routines ? (
          <Skeleton className="h-32 rounded-2xl" />
        ) : routines.length === 0 ? (
          <Card className="py-2">
            <Empty className="border-0 py-10">
              <EmptyHeader>
                <EmptyMedia variant="icon"><CalendarClock /></EmptyMedia>
                <EmptyTitle>{t('ARDI.ROUTINES.EMPTY')}</EmptyTitle>
                <EmptyDescription>{t('ARDI.ROUTINES.EMPTY_HINT')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </Card>
        ) : (
          <section className="flex flex-col gap-3" data-testid="routines-list">
            <h2 className="text-sm font-medium">{t('ARDI.ROUTINES.YOURS')}</h2>
            {routines.map((r) => (
              <Card key={r.uuid} className="gap-0 py-0" data-testid="routine-card">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-background text-muted-foreground">
                    <CalendarClock className="size-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">{r.title}</p>
                      {r.lastStatus === 'failed' && (
                        <Badge variant="outline" className="gap-1.5">
                          <span className="size-1.5 rounded-full bg-destructive" aria-hidden />
                          {t('ARDI.ROUTINES.LAST_FAILED')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {scheduleSentence(t as any, r.schedule, lang)}
                      {r.enabled && r.nextRunAt && (
                        <> · {t('ARDI.ROUTINES.NEXT')} <RelativeTime value={r.nextRunAt} /></>
                      )}
                      {!r.enabled && <> · {t('ARDI.ROUTINES.PAUSED')}</>}
                    </p>
                    {r.lastSummary && (
                      <p className="mt-2 line-clamp-2 border-s-2 ps-3 text-xs leading-relaxed text-muted-foreground">{r.lastSummary}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      {r.lastRunAt ? <span>{t('ARDI.ROUTINES.LAST_RUN')} <RelativeTime value={r.lastRunAt} /></span> : <span>{t('ARDI.ROUTINES.NEVER_RAN')}</span>}
                      {r.deliverTo.map((uuid) => {
                        const c = channelByUuid.get(uuid);
                        if (!c) return null;
                        const Icon = CHANNEL_ICONS[c.kind];
                        return (
                          <span key={uuid} className="flex items-center gap-1">
                            <Icon className="size-3" aria-hidden />
                            {c.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1 self-start">
                    <Button variant="outline" size="sm" onClick={() => runNow(r)} disabled={running === r.uuid}>
                      {running === r.uuid ? <Loader2 className="animate-spin" /> : <Play />}
                      {t(running === r.uuid ? 'ARDI.ROUTINES.RUNNING' : 'ARDI.ROUTINES.RUN_NOW')}
                    </Button>
                    <Switch checked={r.enabled} onCheckedChange={(v) => toggle(r, v)} aria-label={t('ARDI.ROUTINES.FIELD.ENABLED') as string} className="mx-2" />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={t('ARDI.COMMON.MORE') as string}>
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => edit(r)}>
                          <Pencil />
                          {t('ARDI.ROUTINES.EDIT')}
                        </DropdownMenuItem>
                        {r.threadUuid && (
                          <DropdownMenuItem onSelect={() => navigate(`/home/chat/${r.threadUuid}`)}>
                            <MessageSquareText />
                            {t('ARDI.ROUTINES.OPEN_CONVERSATION')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(r)}>
                          <Trash2 />
                          {t('ARDI.ROUTINES.DELETE')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            ))}
          </section>
        )}

        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-medium">{t('ARDI.ROUTINES.TEMPLATES')}</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="routine-templates">
            {templates.map((template) => (
              <Card key={template.id} className={cn('gap-0 py-0', used.has(template.id) && 'opacity-60')}>
                <div className="flex h-full flex-col gap-2 p-4">
                  <p className="text-sm font-medium">{tpl(template.id, 'TITLE', template.title)}</p>
                  <p className="text-xs leading-relaxed text-muted-foreground">{tpl(template.id, 'DESC', '')}</p>
                  <p className="text-[11px] text-muted-foreground">{scheduleSentence(t as any, { ...template.schedule, timezone: browserTimezone() }, lang)}</p>
                  <div className="mt-auto pt-2">
                    <Button variant="outline" size="sm" onClick={() => fromTemplate(template)} disabled={full}>
                      <Plus />
                      {t(used.has(template.id) ? 'ARDI.ROUTINES.ADD_AGAIN' : 'ARDI.ROUTINES.ADD')}
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      </div>

      <SidePanel
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={t(editing?.uuid ? 'ARDI.ROUTINES.EDIT_TITLE' : 'ARDI.ROUTINES.NEW_TITLE') as string}
        description={t('ARDI.ROUTINES.EDITOR_DESC') as string}
        width="data-[side=right]:sm:max-w-[600px]"
      >
        {editing && <RoutineEditor initial={editing.draft} channels={channels} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}
      </SidePanel>

      <ConfirmActionDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={t('ARDI.ROUTINES.DELETE_TITLE', { title: removing?.title ?? '' }) as string}
        description={t('ARDI.ROUTINES.DELETE_DESC') as string}
        confirmLabel={t('ARDI.ROUTINES.DELETE') as string}
        destructive
        onConfirm={remove}
      />
    </PageContainer>
  );
};

export default RoutinesPage;
